import { MoreHorizontal, Star } from "lucide-react";
import type { ReactNode } from "react";
import { IconButton } from "../../components/IconButton/IconButton";
import { ContextMenu, type ContextMenuItem } from "../../components/Menu/ContextMenu";
import { SpaceIcon } from "../../components/SpaceIcon/SpaceIcon";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import type { SidebarNavSpaceItem } from "./SidebarNav";

export const ACTIVE_ROW_CLASSES =
  "relative rounded-lg bg-surface-elevated shadow-sm before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-content-primary";

/** Shown on hover/focus in a row's trailing slot, in place of the chat count. */
const HOVER_ACTION =
  "relative z-10 size-6 shrink-0 cursor-pointer place-items-center rounded text-content-tertiary transition-colors btn-focus-ring group-focus-within:grid group-hover:grid";

/**
 * One space in the sidebar: opens on click; the star (shown on hover, or always when starred) pins/unpins it.
 * Optionally a `menu` (right-click, or the "..." button), a `dragHandle` that replaces the icon on hover/focus,
 * and `dragRowProps` to make the whole row itself a drag activator (no visible handle, grab it directly).
 */
export function SidebarSpaceRow({
  space,
  active,
  starLabel,
  onOpen,
  onToggleStar,
  indented = false,
  menu,
  dragHandle,
  dragRowProps,
  detail,
  showStar = true,
}: Readonly<{
  space: SidebarNavSpaceItem;
  active: boolean;
  starLabel: string;
  onOpen: () => void;
  onToggleStar: () => void;
  indented?: boolean;
  menu?: { label: string; ariaLabel?: string; items: ContextMenuItem[] };
  dragHandle?: ReactNode;
  dragRowProps?: React.HTMLAttributes<HTMLDivElement>;
  /** Quiet text after the name, e.g. the folder the space is in. */
  detail?: string;
  /** False leaves only the "..." button on hover, with favoriting in its menu. */
  showStar?: boolean;
}>) {
  const row = (openMenu?: (element: HTMLElement) => void, menuOpen = false) => (
    <div
      {...dragRowProps}
      className={[
        "group relative flex w-full items-center gap-3 rounded-lg py-2 pr-2 text-left transition-colors [font:var(--type-body-xs)]",
        indented ? "pl-6" : "pl-3",
        active
          ? `${ACTIVE_ROW_CLASSES} ![font-weight:var(--weight-medium)] text-content-primary`
          : [
              "text-content-secondary hover:bg-surface-base hover:text-content-primary",
              // Keep the hover look while this row's menu is open, so the actions' backdrop doesn't show as a patch.
              menuOpen ? "bg-surface-base text-content-primary" : "",
            ].join(" "),
      ].join(" ")}
    >
      <button
        type="button"
        aria-label={space.name}
        aria-pressed={active}
        onClick={onOpen}
        className="absolute inset-0 cursor-pointer rounded-lg btn-focus-ring"
      />
      {dragHandle ? (
        <span className="relative grid shrink-0 place-items-center">
          <span className="group-focus-within:invisible group-hover:invisible">
            <SpaceIcon icon={space.icon} hue={space.hue as number} size="sm" />
          </span>
          <span className="invisible absolute inset-0 z-10 grid place-items-center group-focus-within:visible group-hover:visible">
            {dragHandle}
          </span>
        </span>
      ) : (
        <SpaceIcon icon={space.icon} hue={space.hue as number} size="sm" />
      )}
      <span
        className={[
          "min-w-0 flex-1 truncate",
          // The "..." and star overlay the end of the name; leave their overlap clear so the ellipsis stays visible.
          openMenu && menu && showStar ? (menuOpen ? "pr-5" : "group-focus-within:pr-5 group-hover:pr-5") : "",
        ].join(" ")}
      >
        {space.name}
        {detail && <span className="ml-2 text-content-tertiary">{detail}</span>}
      </span>
      {/* The count and the hover actions share one fixed box, so hovering never changes the name's width. */}
      <span className="relative -my-1 size-6 shrink-0">
        <span
          className={[
            "invisible absolute inset-y-0 right-0 z-10 flex items-center gap-1 rounded-lg pl-1",
            active ? "bg-surface-elevated" : "bg-surface-base",
            menuOpen ? "visible" : "group-focus-within:visible group-hover:visible",
          ].join(" ")}
        >
          {openMenu && menu && (
            <Tooltip content={menu.label}>
              <IconButton
                icon={<MoreHorizontal />}
                aria-label={menu.ariaLabel ?? menu.label}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                variant="ghost"
                flat
                size="sm"
                onClick={(event) => openMenu(event.currentTarget)}
              />
            </Tooltip>
          )}
          {showStar && (
            <Tooltip content={starLabel}>
              <button
                type="button"
                aria-label={starLabel}
                aria-pressed={space.pinned}
                onClick={onToggleStar}
                className={`${HOVER_ACTION} hover:text-warning`}
              >
                <Star size={13} className={space.pinned ? "fill-current" : ""} />
              </button>
            </Tooltip>
          )}
        </span>
        <span
          className={[
            "pointer-events-none absolute inset-y-0 right-0 my-auto grid size-6 place-items-center tabular-nums text-xs text-content-tertiary",
            menuOpen ? "invisible" : "group-focus-within:invisible group-hover:invisible",
          ].join(" ")}
        >
          {space.chatCount}
        </span>
      </span>
    </div>
  );

  if (!menu) return row();
  return <ContextMenu items={menu.items}>{(openFrom, isOpen) => row(openFrom, isOpen)}</ContextMenu>;
}
